import { forwardRef } from 'react';

import type { FieldConfidenceResponse } from '../api';
import type { FieldEditState } from '../hooks/useFieldEdit';
import { hasIssue } from '../hooks/useFieldAggregates';
import { AccordionFieldShell } from './AccordionFieldShell';
import { ConfidenceMeter } from './ConfidenceMeter';
import { TableValueView } from './TableValueView';

interface TableFieldRowProps {
  field: FieldConfidenceResponse;
  edit: FieldEditState | undefined;
  highlightedFieldName: string | null;
}

/**
 * Renders a Table-typed field as an accordion. The header surfaces the field's confidence
 * and row count so the operator can decide whether to expand without first opening the
 * table. The body is the existing read-only `TableValueView`.
 *
 * Smart-auto open: when the field has any warning/critical status or has been manually
 * edited (`hasIssue`), the accordion starts open so the operator sees the rows that
 * need attention without an extra click.
 */
export const TableFieldRow = forwardRef<HTMLDivElement, TableFieldRowProps>(function TableFieldRow(
  { field, edit, highlightedFieldName },
  ref,
): JSX.Element {
  const effectiveValue = edit !== undefined ? edit.value : field.value;
  const rows = isTableValue(effectiveValue) ? effectiveValue : [];
  const manuallyEdited = edit?.manuallyEdited ?? false;
  const defaultOpen = hasIssue(field, edit !== undefined ? { [field.name]: edit } : {});

  return (
    <AccordionFieldShell
      ref={ref}
      fieldName={field.name}
      title={field.name}
      subtitle={rows.length > 0 ? `${rows.length} filas` : 'Sin filas detectadas'}
      leading={<ConfidenceMeter score={field.score} status={field.status} manuallyEdited={manuallyEdited} />}
      badge={
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700 ring-1 ring-slate-200">
          Tabla
        </span>
      }
      meta={
        field.issue != null && field.issue.length > 0 ? (
          <p className="text-xs text-amber-700">{field.issue}</p>
        ) : undefined
      }
      defaultOpen={defaultOpen}
      highlighted={highlightedFieldName === field.name}
    >
      <TableValueView rows={rows} />
    </AccordionFieldShell>
  );
});

/**
 * Same loose type guard as `FieldRow` — a table value lands as an array of plain
 * objects. Duplicated here to keep TableFieldRow self-contained; the FieldRow copy
 * stays for grandchild tables nested inside groups.
 */
function isTableValue(value: unknown): value is ReadonlyArray<Record<string, unknown>> {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every(
    (row) => typeof row === 'object' && row !== null && !Array.isArray(row),
  );
}
